import { MINUTE } from "@app/constants";
import { createSelector } from "@reduxjs/toolkit";
import { Trans } from "@lingui/macro";

export const simpleSetPayload = (stateField) => (state, action) => { state[stateField] = action.payload; };
export const simpleSetSubField = (stateField, subField) => (state, action) => {
  state[stateField] = state[stateField] || {};
  state[stateField][subField] = action.payload;
};
export const objectSetPayload = stateField => (state, action) => ({
  ...state,
  [stateField]: {
    ...state[stateField],
    ...action.payload,
  },
});
export const removeObject = stateField => (state, action) => { state[stateField] = state[stateField].filter(item => item.id !== action.payload); };

export const createSimpleSelector = (slice, fieldName) => createSelector([state => state[slice]?.[fieldName]], field => field);
export const createSimpleSubFieldSelector = (slice, fieldName, subField) => createSelector([state => state[slice]?.[fieldName]?.[subField]], sField => sField);

export const SECOND = 1000;
export const HOUR = MINUTE * 60;

export const STUDENT_PROFILE_DIALOG_Z_INDEX = 1501;
export const STUDENT_PROFILE_TOOLTIP_Z_INDEX = STUDENT_PROFILE_DIALOG_Z_INDEX + 100;
export const ACTIVITY_LIST_PERSIST_DELAY = SECOND * 5;
export const GAMEPLAYLIST_PERSIST_DELAY = SECOND * 5;
export const ACTIVITY_LIBRARY_DATA_MAX_TIME = HOUR * 2;

export const ERR_LOADING_TREATMENT = "Oops... There's a problem and we can't start the session. Go back to the calendar page and try again";
export const WRONG_TREATMENT_ERR_MSG = "You are not assigned to this session, please check your class code";
export const DUPLICATE_SIGN_IN_ERR_MSG = <Trans>You are already signed in to this session</Trans>;
export const UNKNOWN_WS_ERROR = <Trans>Oops...there was a problem and you can&apos;t continue with the session</Trans>;
export const LOCKED_TXT = <Trans>Student screen is locked</Trans>;
export const RECORDING_TEXT = "Recording in progress";
export const RECORDING_PAUSED_TEXT = "Recording is paused";
export const ASSIGN_BUTTON_TEXT = <Trans>Assign to students</Trans>;
export const ASSIGN_BUTTON_TOOLTIP_TEXT = <Trans>Click here to assign to students</Trans>;

export const DRAFT_SAVED_TXT = "The notes draft is saved";
export const MINIMAL_PRESENCE_REQUIREMENT_TXT = <Trans>At least one student should be logged in or marked as arrived before you can finalize this session</Trans>;
export const SESSION_NOT_SAVED_TXT = <Trans>The session was not saved. Please try again</Trans>;
export const SESSION_SAVED_TXT = <Trans>The session is saved</Trans>;
export const RESOURCE_PERMISSION_DENIED = "Your microphone is off";
export const HOW_TO_ACTIVATE_TXT = "how to activate it";

export const WS_STUDENT_LIVE_CHECK_INTERVAL = SECOND * 9;
export const WS_STUDENT_LIVE_CHECK_TOLERANCE = WS_STUDENT_LIVE_CHECK_INTERVAL * 2.1;
export const WS_TEACHER_LIVE_CHECK_INTERVAL = SECOND * 9;
export const WS_TEACHER_LIVE_CHECK_TOLERANCE = WS_TEACHER_LIVE_CHECK_INTERVAL * 2.1;
export const WS_TREATMENT_PING_INTERVAL = SECOND * 2.5;
export const WS_ERROR_THRESHOLD = 3;

export const DRAFT_DELAY = 1200;
export const CIRCULAR_PROGRESS_DELAY = 1000;

export const PREVIEW_BOARD_ID = -1;
export const SELF_PRACTICE_BOARD_ID = -2;

export const BOTTON_TYPES = {
  ARRIVED: "arrived",
  VIDEO: "video",
  CODE: "code",
};

export const H5P_SHUFFLE_SEED_OPTIONS = {
  NONE: 0,
  NEW: 1,
  EXISTING: 2,
};

export const H5P_FRAME_SIZE = { width: 700, height: 560 };

export const INDICATION_TYPES = {
  LOCK: "lock",
  MUTE: "mute",
  PAUSE: "pause",
  MUTE_PAUSE: "muteandpause",
  PERMISSION: "error",
  ERROR: "error",
  WATCHING: "watching",
  INFO: "info",
  RECORDING: "recording",
  RECORDING_PAUSE: "recordingpause",
};

//articulation
export const MAX_WORDS = 30;
export const PREVIEW_BOARD_LIMIT = 2;
export const RECORDING_TIMEOUT = 60000;
export const PREVIEW_ARTICULATION_MSG = "To show cards: {nl}1. Assign the activity to students {nl}2. Select a student to work with";

// ttr
export const SPEECH_START_MSG = "Press Play to start";
export const SPEECH_WAITING_MSG = "Waiting for student";
export const SPEECH_RECORDING_MSG = "Recording";
export const SPEECH_RECORDING_NO_AUDIO_MSG = "Transcript and audio are not available";
export const PREVIEW_SPEECH_MSG = "To show a real-time language analysis: assign the{nl} activity to students and select a student to work with";
export const NO_ONLINE_STUDENTS_TTR_MSG = "Real-time language analysis is only available with online students";
export const NO_ONLINE_STUDENTS_ARTICULATION_MSG = "Showing cards is only available with online students";
export const WAITING_FOR_STUDENT_MSG = "Waiting for student to click \"Show me\"";
export const WAITING_FOR_SPEECH = "Waiting for student to speak";
export const MAX_RETRIES_SPEECH_RECOGNITION = 3;
export const NO_SPEECH_ERR = "no-speech";
export const NO_TRANSCRIPTION = "transcription is unavailable";
export const NO_PERMISSION_MSG = `The student's microphone is disabled, so the ${NO_TRANSCRIPTION}`;
export const MIC_DISABLED = "The student's microphone is disabled";
export const NO_PERMISSION_MSG_TTR = `${MIC_DISABLED}, so the ${NO_TRANSCRIPTION}`;

export const RECORDING_STATUS = {
  WAITING: 1,
  RECORDING: 2,
  FINISHED: 3,
};

//video
export const OPENTOK_API_KEY = "47490461";
export const MAX_VIDEO_STUDENTS = 8;
export const VIDEO_LOG_INTERVAL_TIME = SECOND * 60;

export const VIDEO_STUDENT_STATUS = {
  VIDEO_ON: 1,
  VIDEO_PENDING: 2,
  VIDEO_OFF_CANT_INVITE: 3,
  VIDEO_OFF_CAN_INVITE: 4,
};

export const TEACHER_VIDEO_APPROVAL_STATUS = {
  NO_STATUS: 0,
  NOT_APPROVED: 1,
  PENDING: 2,
  APPROVED: 3,
};

export const OPENTOK_ERRORS = {
  OT_HARDWARE_UNAVAILABLE: "HARDWARE_UNAVAILABLE",
  OT_USER_MEDIA_ACCESS_DENIED: "USER_MEDIA_ACCESS_DENIED",
  NO_OPENTOK_CONNECTION: "NO_OPENTOK_CONNECTION",
  OTHER: "OTHER",
};

export const OPENTOK_ACCESS_DENIED_ERROR_CODE = 1500;

export const STREAM_ERRORS = {
  HARDWARE_UNAVAILABLE: 1,
  USER_MEDIA_ACCESS_DENIED: 2,
  OTHER: 3,
  NO_OPENTOK_CONNECTION: 4,
  USER_SCREEN_ACCESS_DENIED: 5,
};

export const VIDEO_PENDING_SECONDS = 30;

export const DEFAULT_YOUTUBE_VOLUME = 40;
export const SHARESCREEN_STATUS = {
  SHARESCREEN_ON: 1,
  SHARESCREEN_PENDING: 2,
  SHARESCREEN_OFF: 3,
};

export const LOCAL_STORAGE_DONT_SHOW_ASSIGN_INFO = "dont-show-assign-info";
export const LOCAL_STORAGE_SELECTED_AUDIO_DEVICE_ID = "selected-audio-device-id";

// Tech Assist
export const OS_IOS = "OS_IOS";
export const OS_ANDROID = "OS_ANDROID";
export const OS_WINDOWS = "OS_WINDOWS";
export const OS_WINDOWS_PHONE = "OS_WINDOWS_PHONE";
export const OS_MAC_OS = "OS_MAC_OS";

export const DEVICE_MOBILE = "MOBILE";
export const DEVICE_TABLET = "DEVICE_TABLET";
export const DEVICE_SMARTTV = "DEVICE_SMARTTV";
export const DEVICE_CONSOLE = "DEVICE_CONSOLE";
export const DEVICE_WEARBLE = "DEVICE_WEARBLE";
export const DEVICE_EMBEDDED = "DEVICE_EMBEDDED";
export const DEVICE_BROWSER = "DEVICE_BROWSER";

export const BROWSER_CHROME = "BROWSER_CHROME";
export const BROWSER_FIREFOX = "BROWSER_FIREFOX";
export const BROWSER_OPERA = "BROWSER_OPERA";
export const BROWSER_SAFARI = "BROWSER_SAFARI";
export const BROWSER_YANDEX = "BROWSER_YANDEX";
export const BROWSER_EXPLORER = "BROWSER_EXPLORER";
export const BROWSER_CHROMIUM = "BROWSER_CHROMIUM";
export const BROWSER_EDGE = "BROWSER_EDGE";
export const BROWSER_IE = "BROWSER_IE";
export const BROWSER_MOBILE_SAFARI = "BROWSER_MOBILE_SAFARI";
export const BROWSER_EDGE_CHROMIUM = "BROWSER_EDGE_CHROMIUM";
export const BROWSER_MIUI = "BROWSER_MIUI";
export const BROWSER_SAMSUNG = "BROWSER_SAMSUNG";

//react-device-detect translation
export const OS_NAME_DICT = {
  iOS: OS_IOS,
  Android: OS_ANDROID,
  "Windows Phone": OS_WINDOWS_PHONE,
  Windows: OS_WINDOWS,
  "Mac OS": OS_MAC_OS,
};

export const DEVICE_TYPE_DICT = {
  mobile: DEVICE_MOBILE,
  tablet: DEVICE_TABLET,
  smarttv: DEVICE_SMARTTV,
  console: DEVICE_CONSOLE,
  wearable: DEVICE_WEARBLE,
  embedded: DEVICE_EMBEDDED,
  browser: DEVICE_BROWSER,
};

export const BROWSER_TYPE_DICT = {
  Chrome: BROWSER_CHROME,
  Firefox: BROWSER_FIREFOX,
  Opera: BROWSER_OPERA,
  Yandex: BROWSER_YANDEX,
  Safari: BROWSER_SAFARI,
  "Internet Explorer": BROWSER_EXPLORER,
  Edge: BROWSER_EDGE,
  Chromium: BROWSER_CHROMIUM,
  IE: BROWSER_IE,
  "Mobile Safari": BROWSER_MOBILE_SAFARI,
  "Edge Chromium": BROWSER_EDGE_CHROMIUM,
  "MIUI Browser": BROWSER_MIUI,
  "Samsung Browser": BROWSER_SAMSUNG,
};

export const TREATMENT_EVENT_SOURCES = {
  FRONT: 1,
  BACK: 2,
  OPENTOK: 3,
};

export const TECH_INDICATIONS = {
  DEVICE_SUPPORT: "error_tech_assist_device_support",
  OS_SUPPORT: "error_tech_assist_os_support",
  BROWSER_SUPPORT: "error_tech_assist_browser_support",
  OS_VERSION_SUPPORT: "error_tech_assist_os_version_support",
  BROWSER_VERSION_SUPPORT: "error_tech_assist_browser_version_support",
  INTERNET_CONNECTION: "error_tech_assist_internet_connection",
  CPU: "error_tech_assist_cpu",
  PERMISSIONS: "error_tech_assist_permissions",
  USER_FOCUS: "error_tech_assist_user_focus",
  OPENTOK_STREAM: "error_tech_assist_opentok_stream",
};

export const STUDENT_GOAL_STATUS = {
  OPEN_STATUS: 1,
  ACHIEVED_STATUS: 2,
  INACTIVE_STATUS: 3,
};

export const SMART_TEXT_WIDGET_TYPE = "SmartTextWidget";
